/*
 * Which business is this app for, and where does its API live.
 *
 * The web build and the native build answer the first question differently, and
 * that difference is the only structural thing separating them:
 *
 *   Web    one bundle serves every tenant. The slug is the first label of the
 *          hostname, exactly as apps/web/src/middleware.ts reads it, so
 *          `nisha-salon.blooq.eastlaunch.com` and the Next app agree without
 *          either being told.
 *   Native an installed app has no hostname to read — a Play Store listing IS
 *          one business — so the slug is baked in at build time with
 *          VITE_BLOOQ_SLUG.
 *
 * Nothing here validates the slug. The API returns the same `not_found` for an
 * unknown business and one that has stopped being served (see the note in
 * apps/web/src/app/api/v1/config/route.ts), and resolving that difference
 * locally would be inventing an answer the server deliberately withholds.
 */

import { Capacitor } from '@capacitor/core'

/** Must match ROOT_DOMAIN in the root .env — the subtree tenants live under. */
const ROOT_DOMAIN = import.meta.env.VITE_ROOT_DOMAIN || 'blooq.eastlaunch.com'

/**
 * Subdomains that are ours, not a business's.
 *
 * Mirrors SYSTEM_HOSTS in apps/web/src/lib/domains.ts. If this app is ever
 * loaded from one of them, it has no tenant — treating `api` as a business
 * would send every request looking for a salon called "api".
 */
const SYSTEM_HOSTS = new Set(['api', 'app', 'admin', 'widget', 'www'])

/** Remembered only so a `?slug=` used once in development survives a reload. */
const SLUG_KEY = 'blooq.slug'

export const isNative = Capacitor.isNativePlatform()

/** The platform Ionic will style itself as. Used for install copy, not layout. */
export const platform = Capacitor.getPlatform()

function fromHostname(): string | null {
  const host = window.location.hostname

  // Production: <slug>.blooq.eastlaunch.com
  if (host.endsWith(`.${ROOT_DOMAIN}`)) {
    const label = host.slice(0, -(ROOT_DOMAIN.length + 1))
    // Only the leftmost label is a slug. Anything deeper is not a shape we serve.
    if (label && !label.includes('.') && !SYSTEM_HOSTS.has(label)) return label
    return null
  }

  // Development: <slug>.localhost, which Chrome and Firefox resolve without
  // a hosts-file entry.
  if (host.endsWith('.localhost')) {
    const label = host.slice(0, -'.localhost'.length)
    if (label && !label.includes('.') && !SYSTEM_HOSTS.has(label)) return label
  }

  return null
}

function remembered(): string | null {
  try {
    return window.localStorage.getItem(SLUG_KEY)
  } catch {
    // Private mode, or site data blocked. Not fatal — every other source still
    // works, and on the web the hostname is the real answer anyway.
    return null
  }
}

function remember(slug: string) {
  try {
    window.localStorage.setItem(SLUG_KEY, slug)
  } catch {
    // See above.
  }
}

/**
 * The business this run of the app is for, or null when there is no way to tell.
 *
 * Order matters. A build pinned to one tenant must not be redirectable at
 * another by a query string — that would turn a shipped native app into an
 * open renderer for any business on the platform, complete with their branding.
 */
export function resolveSlug(): string | null {
  const pinned = import.meta.env.VITE_BLOOQ_SLUG
  if (pinned) return pinned

  const fromHost = fromHostname()
  if (fromHost) return fromHost

  /*
   * `?slug=` is a DEVELOPMENT affordance and is gated on that.
   *
   * On a tenant's real origin the hostname has already answered, so this can
   * only ever fire on a build where it did not — and there, accepting a slug
   * from the URL would let anyone hand out a link that renders someone else's
   * business inside this app.
   */
  if (import.meta.env.DEV) {
    const asked = new URLSearchParams(window.location.search).get('slug')
    if (asked) {
      remember(asked)
      return asked
    }
    return remembered()
  }

  return null
}

/** Development only: switch the app to another business and reload. */
export function setDevSlug(slug: string) {
  remember(slug)
  window.location.replace(`${window.location.pathname}?slug=${encodeURIComponent(slug)}`)
}

/**
 * Where the flow API lives.
 *
 * Empty on the web, and that emptiness is load-bearing: the app is served from
 * the tenant's own origin, so `/api/v1/...` is same-origin and the booking path
 * costs no preflight. A native build has no origin worth resolving against and
 * must be given an absolute base — vite.config.ts refuses to build without one.
 */
export const apiBase = (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '')

/**
 * The tenant's public web origin, for links that are documents rather than API
 * calls. Today that is the `.ics` file, which the Next app serves per tenant.
 */
export const webBase = (import.meta.env.VITE_WEB_BASE || '').replace(/\/$/, '')

export const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''
